/**
 * Business rules for learner names, specifically for Zoom compatibility.
 *
 * Zoom doesn't accept certain Unicode character ranges:
 * - Mathematical Alphanumeric Symbols (U+1D400-U+1D7FF) - like 𝓐𝓑𝓒
 * - Private Use Area (U+E000-U+F8FF) - like  (U+F8FF)
 * - Private Use Area Extended (U+F0000-U+FFFFD)
 */

/**
 * Unicode ranges that are not allowed in learner names for Zoom compatibility
 */
export const DISALLOWED_LEARNER_NAME_RANGES = [
  {
    name: "Mathematical_Alphanumeric",
    start: 0x1d400,
    end: 0x1d7ff,
    description: "Mathematical Alphanumeric Symbols (like 𝓐𝓑𝓒)",
  },
  {
    name: "Private_Use_Area",
    start: 0xe000,
    end: 0xf8ff,
    description: "Private Use Area (like  U+F8FF)",
  },
  {
    name: "Private_Use_Area_Extended",
    start: 0xf0000,
    end: 0xffffd,
    description: "Private Use Area Extended",
  },
] as const;

/** Mathematical Alphanumeric Symbols: Latin letters end before Greek (1D6A4) */
const MATH_LATIN_END = 0x1d6a4;
/** Mathematical digit ranges (0-9 in various styles) */
const MATH_DIGIT_RANGES: Array<{ start: number; end: number }> = [
  { start: 0x1d7ce, end: 0x1d7d7 }, // Bold
  { start: 0x1d7d8, end: 0x1d7e1 }, // Double-struck
  { start: 0x1d7e2, end: 0x1d7eb }, // Sans-serif
  { start: 0x1d7ec, end: 0x1d7f5 }, // Sans-serif bold
  { start: 0x1d7f6, end: 0x1d7ff }, // Monospace
];

/**
 * Converts a single Mathematical Alphanumeric code point to ASCII equivalent.
 * Latin letters (e.g. 𝓗 U+1D4D7) -> A-Za-z, digits -> 0-9.
 * Returns "" if the code point is in the block but has no ASCII equivalent (e.g. Greek).
 */
function mathematicalCodePointToAscii(codePoint: number): string {
  // Latin letters: U+1D400–U+1D6A3 (Bold, Italic, Script, Fraktur, etc. A-Z and a-z)
  if (codePoint >= 0x1d400 && codePoint < MATH_LATIN_END) {
    const rel = codePoint - 0x1d400;
    const mod = rel % 52;
    if (mod < 26) {
      return String.fromCodePoint(0x41 + mod); // A-Z
    }
    return String.fromCodePoint(0x61 + (mod - 26)); // a-z
  }

  // Mathematical digits 0-9
  for (const { start, end } of MATH_DIGIT_RANGES) {
    if (codePoint >= start && codePoint <= end) {
      return String.fromCodePoint(0x30 + (codePoint - start)); // '0'-'9'
    }
  }

  return "";
}

/**
 * Checks if a character is in any of the disallowed ranges
 */
function isDisallowedCharacter(char: string): boolean {
  const codePoint = char.codePointAt(0);
  if (codePoint === undefined) {
    return false;
  }

  return DISALLOWED_LEARNER_NAME_RANGES.some(
    range => codePoint >= range.start && codePoint <= range.end,
  );
}

/**
 * Checks if a name contains any disallowed characters
 */
export function hasDisallowedCharacters(
  name: string | null | undefined,
): boolean {
  if (!name) {
    return false;
  }

  for (const char of name) {
    if (isDisallowedCharacter(char)) {
      return true;
    }
  }

  return false;
}

/** Return Unicode code points of a string. */
function getCodePoints(s: string): number[] {
  const out: number[] = [];
  for (let i = 0; i < s.length; ) {
    const point = s.codePointAt(i);
    if (point === undefined) {
      break;
    }
    out.push(point);
    i += point > 0xffff ? 2 : 1; // handling UTF-16 surrogate pairs
  }
  return out;
}

/**
 * Sanitizes a learner name: converts Mathematical Alphanumeric Symbols to ASCII
 * (e.g. 𝓐𝓑𝓒 → ABC), removes Private Use Area characters, then normalizes spaces.
 * @param name The name to sanitize
 * @returns The sanitized name
 */
export function sanitizeLearnerName(name: string): string {
  let result = "";

  for (const codePoint of getCodePoints(name)) {
    // Mathematical Alphanumeric: convert to ASCII where possible (Latin A–Z, a–z, digits 0–9)
    if (codePoint >= 0x1d400 && codePoint <= 0x1d7ff) {
      const ascii = mathematicalCodePointToAscii(codePoint);
      if (ascii) {
        result += ascii;
      }
      continue;
    }

    // Private Use Area: remove
    if (
      (codePoint >= 0xe000 && codePoint <= 0xf8ff) ||
      (codePoint >= 0xf0000 && codePoint <= 0xffffd)
    ) {
      continue;
    }

    result += String.fromCodePoint(codePoint);
  }

  // Collapse multiple spaces to single space and trim
  result = result.replace(/\s+/g, " ").trim();

  return result;
}
